/* Ported from the vhc prototype's kit (src/pages/AIGenerated/vhc/components): its copy
   had grown past ours, so the fuller version moved here and became the only one. */
import clsx from 'clsx';
import type { ReactNode } from 'react';
import { BiMinus, BiPlus, BiTrendingDown, BiTrendingUp } from 'react-icons/bi';

import { sparklinePoints } from '@/components';

import { Card } from '../Card/Card';
import { Skeleton } from '../Skeleton';

import s from './StatTile.module.scss';

type TrendDirection = 'up' | 'down' | 'flat';

export interface StatTileProps {
  label: string;
  /**
   * The KPI value. `undefined` means "still loading" (a skeleton is shown —
   * never a fake "0"). `null` means the data resolved but is genuinely
   * absent, rendered as an em dash.
   */
  value?: string | number | null;
  sublabel?: string;
  /**
   * Holds the sublabel's line with a bone while loading, for a caller whose sublabel is
   * derived from the same data as the value — otherwise the tile grows when it arrives.
   */
  reserveSublabel?: boolean;
  icon?: ReactNode;
  trend?: { direction: TrendDirection; label: string };
  loading?: boolean;
  /**
   * Optional trend series (W3.8): 2+ points render as a 40×16 inline SVG
   * polyline beside the value, tinted to match the trend direction.
   */
  sparkline?: number[];
  /** Drill-in handler; makes the whole tile interactive. */
  onClick?: () => void;
  /**
   * Drill-in destination (P3/A6). Preferred over `onClick` for a tile that
   * only navigates — the tile then announces as a link to that route instead
   * of as an unnamed button.
   */
  to?: string;
  className?: string;
  /** Smaller value type — for tiles whose value is text, not a KPI number. */
  compact?: boolean;
  /** Larger value, label and sublabel type on desktop. */
  large?: boolean;
  /**
   * What the tile says when there is nothing to count yet (W3.x). A "0" over a
   * flat sparkline reports an absence and offers nothing to do about it; on a
   * fresh account that is the whole dashboard. Given this, the tile drops the
   * number and the trend line and invites the first one instead.
   *
   * The prompt is a span, not a button: the whole tile is already the control
   * that goes there, and a button inside a button is a trap for keyboard and
   * screen-reader users alike.
   */
  empty?: { line?: string; action: string };
  /** Defaults to a numeric zero; set it for values that are formatted strings. */
  isEmpty?: boolean;
}

const trendIcon: Record<TrendDirection, ReactNode> = {
  up: <BiTrendingUp aria-hidden="true" />,
  down: <BiTrendingDown aria-hidden="true" />,
  flat: <BiMinus aria-hidden="true" />,
};

const trendClass: Record<TrendDirection, string> = {
  up: s.trendUp,
  down: s.trendDown,
  flat: s.trendFlat,
};

export const StatTile = ({
  label,
  value,
  sublabel,
  reserveSublabel,
  icon,
  trend,
  loading,
  sparkline,
  onClick,
  to,
  className,
  compact,
  large,
  empty,
  isEmpty,
}: StatTileProps) => {
  const showSkeleton = loading || value === undefined;
  const blank = !showSkeleton && !!empty && (isEmpty ?? value === 0);
  const showSpark = !showSkeleton && !blank && sparkline !== undefined && sparkline.length >= 2;

  return (
    <Card
      className={clsx(s.tile, large && s.large, className)}
      onClick={to ? undefined : onClick}
      to={to}
    >
      <div className={s.header}>
        <span className={clsx('body-small', s.label)}>{label}</span>
        {icon && <span className={s.icon}>{icon}</span>}
      </div>

      {showSkeleton ? (
        <div className={s.valueRow}>
          <Skeleton variant="text" width={120} className={s.valueBone} />
        </div>
      ) : blank ? (
        <div className={s.empty}>
          {empty!.line && <span className={clsx('body-small', s.emptyLine)}>{empty!.line}</span>}
          <span className={s.emptyAction}>
            <BiPlus aria-hidden="true" />
            {empty!.action}
          </span>
        </div>
      ) : (
        <div className={s.valueRow}>
          <span className={clsx('accent-medium', s.value, compact && s.valueCompact)}>
            {value === null ? '—' : value}
          </span>
          {showSpark && (
            <svg
              className={clsx(s.spark, trend && trendClass[trend.direction])}
              width={40}
              height={16}
              viewBox="0 0 40 16"
              fill="none"
              aria-hidden="true"
            >
              <polyline
                points={sparklinePoints(sparkline)}
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      )}

      {!blank && (sublabel || trend) && (
        <div className={s.footer}>
          {trend && (
            <span className={clsx('body-small', s.trend, trendClass[trend.direction])}>
              {trendIcon[trend.direction]}
              {trend.label}
            </span>
          )}
          {sublabel && <span className={clsx('body-small', s.sublabel)}>{sublabel}</span>}
        </div>
      )}

      {showSkeleton && reserveSublabel && !sublabel && !trend && (
        <div className={s.footer}>
          <Skeleton variant="text" width={96} className={s.sublabelBone} />
        </div>
      )}
    </Card>
  );
};
